// src/components/SimpleComponents/SimpleEntityUid.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import SimpleCnpj from './SimpleCnpj';
import SimpleCpf from './SimpleCpf';
import SimpleNipc from './SimpleNipc';
import SimpleNif from './SimpleNif';
import SimpleEin from './SimpleEin';
import SimpleSsn from './SimpleSsn';
import SimpleUid from './SimpleUid';
import useSubscriptionLocale from 'hooks/useSubscriptionLocale';

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Returns the form field name for the entity tax ID based on countryCode + entityType.
 *   PT  company -> 'nipc'   PT  person -> 'nif'
 *   BR  company -> 'cnpj'   BR  person -> 'cpf'
 *   US  company -> 'ein'    US  person -> 'ssn'
 *   *   -> 'uid'
 */
export const getEntityUidFieldName = (countryCode, entityType = 'company') => {
  const isPerson = entityType === 'person';
  if (countryCode === 'PT') return isPerson ? 'nif' : 'nipc';
  if (countryCode === 'US') return isPerson ? 'ssn' : 'ein';
  if (countryCode === 'BR') return isPerson ? 'cpf' : 'cnpj';
  return 'uid';
};

/**
 * Derives the 2-letter uppercase country code from a BCP-47 locale string.
 * 'pt-PT' -> 'PT',  'pt-BR' -> 'BR',  'en-US' -> 'US'
 */
export const countryCodeFromLocale = locale =>
  locale?.split('-')[1]?.toUpperCase() || 'BR';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************

/**
 * Universal entity tax-ID input.
 * Routes by countryCode + entityType (company or person).
 *   PT company -> SimpleNipc  PT person -> SimpleNif
 *   BR company -> SimpleCnpj  BR person -> SimpleCpf
 *   US company -> SimpleEin   US person -> SimpleSsn
 *   *  -> SimpleUid (free-form)
 *
 * API-specific props (searchCnpjByApi, showApiInfo, silentSearch, unique) are
 * only forwarded to components that support them to avoid DOM warnings.
 */
const SimpleEntityUid = ({
  countryCode: countryCodeProp,
  entityType = 'company',
  searchCnpjByApi,
  showApiInfo,
  silentSearch,
  unique,
  ...commonProps
}) => {

  // 1. PROPS, CONTEXTS & GLOBAL STATE
  const { locale } = useSubscriptionLocale();
  const countryCode = countryCodeProp || countryCodeFromLocale(locale);
  const isPerson = entityType === 'person';

  // 8. UI
  if (countryCode === 'PT') {
    if (isPerson) return <SimpleNif name="nif" {...commonProps} />;
    return (
      <SimpleNipc
        name="nipc"
        unique={unique}
        silentSearch={silentSearch}
        searchNipcByApi={searchCnpjByApi}
        {...commonProps}
      />
    );
  }

  if (countryCode === 'US') {
    if (isPerson) return <SimpleSsn name="ssn" {...commonProps} />;
    return <SimpleEin name="ein" {...commonProps} />;
  }

  if (countryCode === 'BR') {
    if (isPerson) {
      return (
        <SimpleCpf
          name="cpf"
          unique={unique}
          searchCpfByApi={searchCnpjByApi}
          {...commonProps}
        />
      );
    }
    return (
      <SimpleCnpj
        name="cnpj"
        searchCnpjByApi={searchCnpjByApi}
        showApiInfo={showApiInfo}
        silentSearch={silentSearch}
        unique={unique}
        {...commonProps}
      />
    );
  }

  // Fallback: free-form unique ID for any other country
  return <SimpleUid {...commonProps} />;
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SimpleEntityUid;
